/* global BigInt:readonly */

// Les facteurs sont exprimés dans l'unité de base de chaque famille.
export const CONVERSION_FAMILIES = Object.freeze([
  Object.freeze({
    value: 'volume', text: 'Volume liquide', baseUnit: 'ml', defaultUnit: 'hl',
    defaultAllowedUnits: Object.freeze(['ml', 'l', 'hl']),
    units: Object.freeze([
      { value: 'ml', text: 'Millilitre (mL)', symbol: 'mL', factor: 1 },
      { value: 'cl', text: 'Centilitre (cL)', symbol: 'cL', factor: 10 },
      { value: 'dl', text: 'Décilitre (dL)', symbol: 'dL', factor: 100 },
      { value: 'l', text: 'Litre (L)', symbol: 'L', factor: 1000 },
      { value: 'hl', text: 'Hectolitre (hL)', symbol: 'hL', factor: 100000 },
      { value: 'm3', text: 'Mètre cube (m³)', symbol: 'm³', factor: 1000000 }
    ].map(Object.freeze))
  }),
  Object.freeze({
    value: 'length', text: 'Longueur', baseUnit: 'mm', defaultUnit: 'cm',
    defaultAllowedUnits: Object.freeze(['mm', 'cm', 'm']),
    units: Object.freeze([
      { value: 'mm', text: 'Millimètre (mm)', symbol: 'mm', factor: 1 },
      { value: 'cm', text: 'Centimètre (cm)', symbol: 'cm', factor: 10 },
      { value: 'dm', text: 'Décimètre (dm)', symbol: 'dm', factor: 100 },
      { value: 'm', text: 'Mètre (m)', symbol: 'm', factor: 1000 },
      { value: 'km', text: 'Kilomètre (km)', symbol: 'km', factor: 1000000 }
    ].map(Object.freeze))
  })
])

export const DEFAULT_CONVERSION_SETTINGS = Object.freeze({
  family: 'volume', storageUnit: 'ml', defaultUnit: 'hl',
  allowUnitSwitch: true, allowedUnits: Object.freeze(['ml', 'l', 'hl']), decimals: 2
})

export function getConversionFamily(family) {
  return CONVERSION_FAMILIES.find(item => item.value === family) || CONVERSION_FAMILIES[0]
}

export function getConversionUnits(family) {
  return getConversionFamily(family).units
}

// Complète les configurations anciennes ou partielles sans modifier leurs objets.
export function normalizeConversionSettings(settings = {}) {
  const source = settings && typeof settings === 'object' && !Array.isArray(settings) ? settings : {}
  const family = getConversionFamily(source.family)
  const known = family.units.map(unit => unit.value)
  const storageUnit = known.includes(source.storageUnit) ? source.storageUnit : family.baseUnit
  const defaultFallback = known.includes(family.defaultUnit) ? family.defaultUnit : family.baseUnit
  const defaultUnit = known.includes(source.defaultUnit) ? source.defaultUnit : defaultFallback
  let allowedUnits = Array.isArray(source.allowedUnits)
    ? [...new Set(source.allowedUnits.filter(unit => known.includes(unit)))]
    : (family.defaultAllowedUnits || [family.baseUnit]).filter(unit => known.includes(unit))
  if (!allowedUnits.includes(defaultUnit)) allowedUnits = [...allowedUnits, defaultUnit]
  const rawDecimals = source.decimals === '' || source.decimals === null ? NaN : Number(source.decimals)
  return {
    family: family.value, storageUnit, defaultUnit,
    allowUnitSwitch: typeof source.allowUnitSwitch === 'boolean' ? source.allowUnitSwitch : true,
    allowedUnits,
    decimals: Number.isInteger(rawDecimals) && rawDecimals >= 0 && rawDecimals <= 6 ? rawDecimals : 2
  }
}

function isEmpty(value) {
  return value === undefined || value === null || (typeof value === 'string' && value.trim() === '')
}

// Le calcul décimal exact évite les faux restes produits par les flottants.
function decimalRatio(value) {
  if (!['number', 'string'].includes(typeof value)) return null
  if (typeof value === 'number' && !Number.isFinite(value)) return null
  const text = String(value).trim().replace(',', '.')
  if (text.length > 128 || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) return null
  const negative = text.startsWith('-')
  const [whole, fraction = ''] = text.replace(/^[+-]/, '').split('.')
  const digits = `${whole || '0'}${fraction}`
  return { numerator: BigInt(digits) * BigInt(negative ? -1 : 1), denominator: BigInt(10) ** BigInt(fraction.length) }
}

function units(settings, displayUnit) {
  const normalized = normalizeConversionSettings(settings)
  const list = getConversionUnits(normalized.family)
  const storage = list.find(unit => unit.value === normalized.storageUnit)
  const display = list.find(unit => unit.value === (displayUnit || normalized.defaultUnit))
  return { normalized, storage, display }
}

// La valeur émise est toujours un entier sûr dans l'unité stockée.
export function toStorageValue(input, settings, displayUnit) {
  if (isEmpty(input)) return { valid: true, value: null, error: '' }
  const ratio = decimalRatio(input)
  const { storage, display } = units(settings, displayUnit)
  if (!ratio || !display) return { valid: false, error: 'Saisissez un nombre valide.' }
  const numerator = ratio.numerator * BigInt(display.factor)
  const denominator = ratio.denominator * BigInt(storage.factor)
  if (numerator % denominator !== BigInt(0)) {
    return { valid: false, error: `Cette valeur représente une fraction de ${storage.symbol}. Saisissez une quantité correspondant à un nombre entier de ${storage.symbol}.` }
  }
  const integer = numerator / denominator
  if (integer > BigInt(Number.MAX_SAFE_INTEGER) || integer < BigInt(Number.MIN_SAFE_INTEGER)) {
    return { valid: false, error: 'Cette valeur dépasse la précision numérique prise en charge.' }
  }
  return { valid: true, value: Number(integer), error: '' }
}

// L'arrondi ne concerne que le texte affiché, jamais la valeur du formulaire.
export function fromStorageValue(value, settings, displayUnit) {
  if (isEmpty(value)) return { valid: true, display: '', error: '' }
  const { normalized, storage, display } = units(settings, displayUnit)
  if (!display) return { valid: false, display: '', error: 'L’unité de conversion est invalide.' }
  const stored = toStorageValue(value, normalized, storage.value)
  if (!stored.valid) return { ...stored, display: String(value) }
  const numerator = BigInt(stored.value) * BigInt(storage.factor)
  const denominator = BigInt(display.factor)
  const negative = numerator < BigInt(0)
  const magnitude = negative ? -numerator : numerator
  const scale = BigInt(10) ** BigInt(normalized.decimals)
  const scaled = magnitude * scale
  let rounded = scaled / denominator
  if ((scaled % denominator) * BigInt(2) >= denominator) rounded += BigInt(1)
  const padded = rounded.toString().padStart(normalized.decimals + 1, '0')
  const text = normalized.decimals
    ? `${padded.slice(0, -normalized.decimals)},${padded.slice(-normalized.decimals)}`
    : padded
  return { valid: true, display: `${negative ? '-' : ''}${text}`, error: '' }
}
